import { useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { ApiError, del, fmtBytes, get, html, patch } from "../lib.js";
import { Badge, Field, Modal, toast, useAction, useLoad } from "../ui.js";

// The website's lectures and files (src/sites/library.ts): talks the owner has given and files they
// hand out, each with a page on the site and a QR code for the last slide or a flyer. Shown only
// when the platform admin has switched the library on for this business, to owners and admins.

const VISIBILITY = {
  gated: ["ok", "For name and number"],
  public: ["neutral", "Open download"],
  hidden: ["muted", "Hidden"],
};
const KIND = { lecture: "Lecture", resource: "File" };

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));

/** The file itself as the request body, like call recordings: no base64 swelling a 25 MB deck. */
async function sendFile(url, method, file, query) {
  const params = new URLSearchParams({ filename: file.name, ...query });
  const res = await fetch(`/api${url}?${params}`, { method, credentials: "same-origin", headers: { "content-type": "application/octet-stream" }, body: file });
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? tr("The upload failed. Try again."));
  return data.file;
}

/** The QR as a PNG, drawn from the SVG the API serves, for programs that don't take SVG. */
async function downloadPng(svgUrl, name) {
  const svg = await (await fetch(svgUrl, { credentials: "same-origin" })).text();
  const img = new Image();
  const src = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1024;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0, 1024, 1024);
  URL.revokeObjectURL(src);
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = name;
  a.click();
}

export function LibraryCard({ t }) {
  const [data, reload, error] = useLoad(() => get(`${t.base}/site/files`), [t.id]);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busy, run] = useAction();
  if (error || !data) return null;

  const remove = async (f) => {
    await run(() => del(`${t.base}/site/files/${f.id}`), tr("Deleted"));
    setConfirming(null);
    reload();
  };
  const replace = (f) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await run(() => sendFile(`${t.base}/site/files/${f.id}/file`, "PUT", file, {}), tr("File replaced. The page and QR code stay the same."));
    reload();
  };

  return html`<section class="card">
    <div class="section-head">
      <h2>${tr("Lectures and files")}</h2>
      ${data.available && data.files.length < data.limits.max_files && html`<button type="button" class="btn small primary" onClick=${() => setEditing({})}>${tr("Upload")}</button>`}
    </div>
    <p class="muted small">${tr("Talks you've given and files you hand out, each with its own page on your website and a QR code for the last slide. A file for name and number turns every download into a WhatsApp lead tagged with the lecture.")}</p>
    ${!data.available && html`<div class="callout small warn">${tr("File uploads aren't available on this platform right now.")}</div>`}
    ${!data.published && html`<div class="callout small">${tr("The pages go live when you publish your website.")}</div>`}
    ${!data.files.length
      ? html`<p class="muted small">${tr("Nothing uploaded yet.")}</p>`
      : html`<div class="link-list">${data.files.map((f) => {
          const qr = `/api${t.base}/site/files/${f.id}/qr.svg`;
          const [kind, label] = VISIBILITY[f.visibility] ?? ["neutral", f.visibility];
          return html`<article class="link-item">
            <img class="link-qr" src=${qr} alt=${tr("QR code for {name}", { name: f.title })} loading="lazy" />
            <div class="link-body">
              <div class="section-head"><strong dir="auto">${f.title}</strong>
                <span><${Badge} kind="neutral">${tr(KIND[f.kind])}</${Badge}> <${Badge} kind=${kind}>${tr(label)}</${Badge}></span></div>
              <div class="muted small" dir="auto">${[f.org, f.held_on].filter(Boolean).join(" · ")} ${f.filename} · ${fmtBytes(f.size_bytes)}</div>
              <code class="small link-url" dir="ltr">${f.page_url}</code>
              <div class="link-stats small">
                <span><strong>${f.downloads_count}</strong> ${tr("downloads")}</span>
                <span><strong>${f.leads_count}</strong> ${tr("leads")}</span>
              </div>
              <div class="form-actions">
                <button type="button" class="btn small" onClick=${() => copy(f.qr_link)}>${tr("Copy link")}</button>
                <a class="btn small" href=${qr} download=${`qr-${f.slug}.svg`}>${tr("QR as SVG")}</a>
                <button type="button" class="btn small" onClick=${() => downloadPng(qr, `qr-${f.slug}.png`).catch(() => toast(tr("We couldn't make the PNG. Download the SVG instead."), "error"))}>${tr("QR as PNG")}</button>
                <button type="button" class="btn small ghost" onClick=${() => setEditing(f)}>${tr("Edit")}</button>
                <label class="btn small ghost">${tr("Replace file")}<input type="file" hidden disabled=${busy} accept=${data.limits.extensions.map((x) => `.${x}`).join(",")} onChange=${replace(f)} /></label>
                ${confirming === f.id
                  ? html`<span class="row" role="group" aria-label=${tr("Confirm delete")}><span class="small">${tr("Delete the file and its page? Printed QR codes will stop working.")}</span>
                      <button type="button" class="btn small danger" disabled=${busy} onClick=${() => remove(f)}>${tr("Delete")}</button>
                      <button type="button" class="btn small ghost" onClick=${() => setConfirming(null)}>${tr("Cancel")}</button></span>`
                  : html`<button type="button" class="btn small ghost" onClick=${() => setConfirming(f.id)}>${tr("Delete")}</button>`}
              </div>
            </div>
          </article>`;
        })}</div>`}
    <p class="muted small">${tr("PDF, PowerPoint, Word, Excel, PNG or JPEG, up to {mb} MB each and {n} in all.", { mb: data.limits.max_bytes / (1024 * 1024), n: data.limits.max_files })}</p>
    ${editing && html`<${FileForm} t=${t} file=${editing} limits=${data.limits} onClose=${() => setEditing(null)} onSaved=${() => { setEditing(null); reload(); }} />`}
  </section>`;
}

function FileForm({ t, file, limits, onClose, onSaved }) {
  const isNew = !file.id;
  const [values, setValues] = useState({
    kind: file.kind ?? "lecture",
    title: file.title ?? "",
    org: file.org ?? "",
    held_on: file.held_on ?? "",
    description: file.description ?? "",
    visibility: file.visibility ?? "gated",
  });
  const [picked, setPicked] = useState(null);
  const [busy, run] = useAction();
  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });
  const lecture = values.kind === "lecture";
  const save = async (e) => {
    e.preventDefault();
    const fields = lecture ? values : { ...values, org: "", held_on: "" };
    if (isNew) await run(() => sendFile(`${t.base}/site/files`, "POST", picked, fields), tr("Uploaded"));
    else await run(() => patch(`${t.base}/site/files/${file.id}`, fields), tr("Saved"));
    onSaved();
  };
  return html`<${Modal} title=${isNew ? tr("Upload a lecture or file") : tr("Edit")} onClose=${onClose}>
    <form onSubmit=${save}>
      ${isNew && html`<${Field} label=${tr("File")}><input type="file" required accept=${limits.extensions.map((x) => `.${x}`).join(",")} onChange=${(e) => setPicked(e.target.files?.[0] ?? null)} /></${Field}>`}
      <${Field} label=${tr("Kind")}><select value=${values.kind} onChange=${set("kind")}><option value="lecture">${tr("Lecture")}</option><option value="resource">${tr("File")}</option></select></${Field}>
      <${Field} label=${tr("Title")}><input required maxlength="120" dir="auto" value=${values.title} onInput=${set("title")} /></${Field}>
      ${lecture && html`<div class="row">
        <${Field} label=${tr("Where it was given")}><input maxlength="120" dir="auto" value=${values.org} onInput=${set("org")} /></${Field}>
        <${Field} label=${tr("Date")}><input type="date" value=${values.held_on} onInput=${set("held_on")} /></${Field}>
      </div>`}
      <${Field} label=${tr("Description")}><textarea rows="3" maxlength="1000" dir="auto" value=${values.description} onInput=${set("description")}></textarea></${Field}>
      <${Field} label=${tr("Who can download it")} hint=${tr("For name and number: visitors leave a name and mobile number first, and become a WhatsApp lead tagged with this item.")}>
        <select value=${values.visibility} onChange=${set("visibility")}>
          <option value="gated">${tr("For name and number")}</option>
          <option value="public">${tr("Open download")}</option>
          <option value="hidden">${tr("Hidden")}</option>
        </select>
      </${Field}>
      ${!isNew && html`<p class="muted small">${tr("The page address and QR code stay the same when you edit, so anything already printed keeps working.")}</p>`}
      <div class="form-actions"><button class="btn primary" disabled=${busy || (isNew && !picked)}>${isNew ? tr("Upload") : tr("Save")}</button><button type="button" class="btn ghost" onClick=${onClose}>${tr("Cancel")}</button></div>
    </form>
  </${Modal}>`;
}
